/**
 * Which saved locations the appointment Where step may offer.
 *
 * The public catalogue (server-side) already narrows locations to Active,
 * not archived and with configured hours. What it cannot say is whether a
 * location has anything to BOOK for the selected service. The authority for
 * that is the service-wide availability window: a location is eligible only
 * when that window carries at least one real, future offer at it with
 * capacity left.
 *
 * Nothing here invents eligibility:
 *   - an offer needs its own `locationId`, a valid `startUtc` before `endUtc`,
 *     a start in the future and a finite `remaining` above zero;
 *   - a location id the catalogue does not carry is never turned into a row;
 *   - catalogue rows are returned verbatim, in catalogue order, and never
 *     de-duplicated: two saved records with the same name and address are two
 *     real records;
 *   - while the window is idle, loading, failed or for another service, the
 *     eligible set is EMPTY, never "every location".
 */

export interface OfferLike {
  locationId?: unknown;
  startUtc?: unknown;
  endUtc?: unknown;
  remaining?: unknown;
}

export interface WindowLike {
  byDay?: Record<string, readonly OfferLike[]> | null;
}

export interface LocationLike {
  id: string;
  virtual?: boolean;
  latitude?: unknown;
  longitude?: unknown;
}

export type EligibilityStatus = "idle" | "loading" | "error" | "ready";

/** Identity of one service-wide window: the slug, the service and the search horizon. */
export function eligibilityKey(slug: unknown, serviceId: unknown, horizonDays: unknown): string {
  const s = String(slug ?? "").trim();
  const v = String(serviceId ?? "").trim();
  if (!s || !v) return "";
  const days = Number(horizonDays);
  return [s, v, Number.isFinite(days) && days > 0 ? Math.round(days) : 60].join("|");
}

/** Whether one offer is a real, future, bookable offer at a named location. */
export function isBookableOffer(slot: OfferLike | null | undefined, nowMs: number): boolean {
  if (!slot || typeof slot !== "object") return false;
  const loc = typeof slot.locationId === "string" ? slot.locationId.trim() : "";
  if (!loc) return false;
  if (typeof slot.startUtc !== "string" || typeof slot.endUtc !== "string") return false;
  const start = Date.parse(slot.startUtc);
  const end = Date.parse(slot.endUtc);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
  if (start <= nowMs) return false;
  return typeof slot.remaining === "number" && Number.isFinite(slot.remaining) && slot.remaining > 0;
}

/** Every location id that carries at least one bookable offer in the window. */
export function offeredLocationIds(window: WindowLike | null | undefined, nowMs: number): Set<string> {
  const ids = new Set<string>();
  const byDay = (window && window.byDay) || {};
  for (const day of Object.keys(byDay)) {
    for (const slot of byDay[day] || []) {
      if (isBookableOffer(slot, nowMs)) ids.add(String(slot.locationId).trim());
    }
  }
  return ids;
}

/**
 * Catalogue rows with a bookable offer, verbatim and in catalogue order.
 * `restriction` is an EXPLICIT configured allow-list for the service; absent
 * (undefined/null) means none is configured, and the offers alone decide.
 */
export function eligibleLocations<T extends LocationLike>(
  locations: readonly T[] | null | undefined,
  offered: ReadonlySet<string>,
  restriction?: readonly string[] | null,
): T[] {
  const allowed = Array.isArray(restriction) ? new Set(restriction.map((id) => String(id))) : null;
  return (locations || []).filter((row) => !!row && !!row.id && offered.has(row.id) && (!allowed || allowed.has(row.id)));
}

/** A row by exact id, or null. Never the first row, never a nearby one. */
export function locationById<T extends LocationLike>(locations: readonly T[] | null | undefined, id: unknown): T | null {
  const key = String(id ?? "");
  if (!key) return null;
  return (locations || []).filter((row) => row && row.id === key)[0] || null;
}

/** Virtual from the catalogue's own flag only. */
export function isVirtualLocation(row: LocationLike | null | undefined): boolean {
  return !!row && row.virtual === true;
}

export function hasCoordinates(row: LocationLike | null | undefined): boolean {
  return !!row && typeof row.latitude === "number" && Number.isFinite(row.latitude)
    && typeof row.longitude === "number" && Number.isFinite(row.longitude);
}

/** Map pins: eligible PHYSICAL rows with a real coordinate pair, values unchanged. */
export function mapPinsFor<T extends LocationLike & { name?: string }>(rows: readonly T[], selectedId: unknown) {
  return rows
    .filter((row) => !isVirtualLocation(row) && hasCoordinates(row))
    .map((row) => ({ id: row.id, latitude: row.latitude as number, longitude: row.longitude as number, label: String(row.name ?? ""), selected: row.id === selectedId }));
}

/** Caption for eligible physical rows the map cannot pin. Says nothing about any other record. */
export function missingPinCaption(rows: readonly LocationLike[]): string | undefined {
  const missing = rows.filter((row) => !isVirtualLocation(row) && !hasCoordinates(row)).length;
  if (!missing) return undefined;
  return missing === 1
    ? "One of the locations listed has no map coordinates on record, so it is not pinned."
    : missing + " of the locations listed have no map coordinates on record, so they are not pinned.";
}

/**
 * The selection that survives an eligible response. A still-eligible
 * selection is kept; a sole eligible location is chosen; anything else is
 * cleared. A kiosk-pinned location is fixed and never replaced.
 */
export function reconcileSelectedLocation(prevLoc: unknown, eligible: readonly LocationLike[], pinnedLoc?: unknown): string {
  const prev = String(prevLoc ?? "");
  if (pinnedLoc && prev === String(pinnedLoc)) return prev;
  if (prev && eligible.some((row) => row.id === prev)) return prev;
  if (eligible.length === 1) return eligible[0].id;
  return "";
}

/** Status of the eligible set for the key the screen currently needs. */
export function eligibilityStatus(
  state: { availState?: unknown; availFor?: unknown },
  expectedKey: string,
): EligibilityStatus {
  if (!expectedKey) return "idle";
  if (state.availState === "error") return "error";
  if (state.availState === "ready" && state.availFor === expectedKey) return "ready";
  return "loading";
}
